// @ts-nocheck
import { mapState, mapRefs } from "./state";
import {
  applyCameraTransform,
  ensureVisibleChunks,
  normalizeCameraBounds,
} from "./camera";

const PAN_MOVE_THRESHOLD = 5;
const SUPPRESS_CLICK_MS = 350;
const WHEEL_IDLE_MS = 160;

function isInteractiveGardenTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.closest) return false;
  return !!el.closest(".hole-spot, .modal-overlay");
}

export function initPanControls(): void {
  const viewport = mapRefs.viewport;
  if (!viewport) return;

  let activePointerId: number | null = null;
  let panStartX = 0;
  let panStartY = 0;
  let panCamX = 0;
  let panCamY = 0;
  let panRafPending = false;
  let panRafId = 0;
  let suppressClickUntil = 0;
  let wheelIdleTimer: ReturnType<typeof setTimeout> | null = null;

  function schedulePanFrame(): void {
    if (panRafPending) return;
    panRafPending = true;
    panRafId = requestAnimationFrame(function () {
      panRafPending = false;
      panRafId = 0;
      if (!mapState.isPanning) return;
      applyCameraTransform();
    });
  }

  function beginPan(e: PointerEvent): void {
    activePointerId = e.pointerId;
    mapState.isPanning = true;
    mapState.panMoved = false;
    viewport.classList.add("is-panning");
    panStartX = e.clientX;
    panStartY = e.clientY;
    panCamX = mapState.camX;
    panCamY = mapState.camY;

    try {
      viewport.setPointerCapture(e.pointerId);
    } catch {
      // Pointer capture can fail if the pointer was already released.
    }
  }

  function movePan(e: PointerEvent): void {
    if (activePointerId !== e.pointerId) return;

    const dx = e.clientX - panStartX;
    const dy = e.clientY - panStartY;
    if (Math.abs(dx) > PAN_MOVE_THRESHOLD || Math.abs(dy) > PAN_MOVE_THRESHOLD) {
      mapState.panMoved = true;
    }

    mapState.camX = panCamX - dx / mapState.zoomLevel;
    mapState.camY = panCamY - dy / mapState.zoomLevel;
    normalizeCameraBounds();
    schedulePanFrame();
  }

  function finishPan(): void {
    mapState.isPanning = false;
    viewport.classList.remove("is-panning");
    if (panRafId) {
      cancelAnimationFrame(panRafId);
      panRafId = 0;
    }
    panRafPending = false;
    // Wheel pan never sets suppressClickUntil; reset so touchend on tombs is not blocked.
    if (activePointerId === null) mapState.panMoved = false;
    normalizeCameraBounds();
    applyCameraTransform({ forceGrass: true });
    ensureVisibleChunks({ mode: "deferred" });
  }

  function endPan(e?: PointerEvent): void {
    if (activePointerId === null) return;
    if (e && activePointerId !== e.pointerId) return;

    const pointerId = activePointerId;
    activePointerId = null;

    if (mapState.panMoved) suppressClickUntil = performance.now() + SUPPRESS_CLICK_MS;

    try {
      viewport.releasePointerCapture(pointerId);
    } catch {
      // The browser may already have released capture on cancel/lostcapture.
    }

    finishPan();
  }

  function normalizeWheelDelta(e: WheelEvent) {
    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) return { x: e.deltaX * 16, y: e.deltaY * 16 };
    if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      return { x: e.deltaX * viewport.clientWidth, y: e.deltaY * viewport.clientHeight };
    }
    return { x: e.deltaX, y: e.deltaY };
  }

  function onWheel(e: WheelEvent): void {
    if ((e.target as HTMLElement).closest(".modal-overlay, #app-main, .pscroll")) return;

    e.preventDefault();
    if (activePointerId !== null) return;

    const delta = normalizeWheelDelta(e);
    mapState.isPanning = true;
    mapState.panMoved = true;
    viewport.classList.add("is-panning");
    mapState.camX += delta.x / mapState.zoomLevel;
    mapState.camY += delta.y / mapState.zoomLevel;
    normalizeCameraBounds();
    schedulePanFrame();

    if (wheelIdleTimer) clearTimeout(wheelIdleTimer);
    wheelIdleTimer = setTimeout(function () {
      wheelIdleTimer = null;
      finishPan();
    }, WHEEL_IDLE_MS);
  }

  viewport.addEventListener("pointerdown", function (e) {
    if (e.button !== 0 || e.isPrimary === false) return;
    if (isInteractiveGardenTarget(e.target)) return;
    beginPan(e);
  });

  viewport.addEventListener("pointermove", function (e) {
    movePan(e);
  });

  viewport.addEventListener("pointerup", endPan);
  viewport.addEventListener("pointercancel", endPan);
  viewport.addEventListener("lostpointercapture", function (e) {
    if (e.buttons !== 0) return;
    endPan(e);
  });
  viewport.addEventListener("wheel", onWheel, { passive: false });
  viewport.addEventListener(
    "touchmove",
    function (e) {
      if (e.cancelable) e.preventDefault();
    },
    { passive: false },
  );

  viewport.addEventListener(
    "click",
    function (e) {
      if (performance.now() > suppressClickUntil) return;
      e.preventDefault();
      e.stopPropagation();
      (e as any).stopImmediatePropagation?.();
      mapState.panMoved = false;
    },
    true,
  );
}
